import type { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { API_URL, IS_PRODUCTION } from 'constants/envs.ts'
import { Capacitor } from '@capacitor/core'
import { Context } from '@sentry/react'
import { Mutex } from 'async-mutex'
import { getActiveAccessToken, getRefreshToken, setTokens } from 'utils/localStorageService.ts'
import { getUserType, identifyUserType } from 'utils/userType.ts'
import { getRefreshTokenUrlForUserType } from 'utils/app.ts'
import { handleLogout, handleMagicSessionLogout } from 'utils/axios.ts'
import { Criticality, IdentifierFormats, Severity } from 'types/logging.ts'
import { Methods, Response } from 'types/request.ts'
import { RequestContext } from 'store/queries/types.ts'
import { log, trackSentryCount } from 'utils/logging.ts'
import { RefreshTokensResponse } from 'store/queries/bolt-api/types.ts'
import { STORAGE_TYPES } from 'types/auth.ts'
import { UserTypes } from 'store/auth/types'
import { isResponseCode } from 'utils/errors.ts'
import { MAX_ERROR_BODY_LENGTH, NO_RESPONSE_STATUSES, OPAQUE_TRANSPORT_ERRORS } from './constants.ts'

// Single shared mutex ensures only one token refresh runs at a time across all API clients
const mutex = new Mutex()

// Derived from fetchBaseQuery so the wrapper keeps FetchBaseQueryMeta in its signature.
// Without it, `meta` in transformErrorResponse would be typed as `{}` and the request context would be unreachable.
type BaseQueryWithReauth = ReturnType<typeof fetchBaseQuery>

const is401 = (error: FetchBaseQueryError): boolean =>
  error.status === 401 || (error.status === 'PARSING_ERROR' && error.originalStatus === 401)

// Only the production API answers a 403 with a body that fails to parse, hiding the status in `originalStatus`
const is403 = (error: FetchBaseQueryError): boolean =>
  error.status === 403 || (error.status === 'PARSING_ERROR' && error.originalStatus === 403 && IS_PRODUCTION)

const resolveUserType = (): UserTypes => getUserType() || identifyUserType()

/**
 * Factory that creates a RTK Query baseQuery with automatic re-authentication on 401. A 403 is a permission failure
 * that never ends the session; it is only retried when it raced a token refresh.
 * Token refresh always targets the main API URL regardless of the provided baseUrl.
 * @param {string} baseUrl - The base URL for the RTK Query client
 */
export const createBaseQueryWithReauth = (baseUrl: string): BaseQueryWithReauth => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      // Resolve the user type the same way the reauth path below does. Reading the token without one leaves the request
      // unauthenticated whenever the store has not hydrated yet, which then surfaces as an unexplained 401.
      const activeAccessToken = getActiveAccessToken(resolveUserType())

      if (activeAccessToken) {
        headers.set('Authorization', `Bearer ${activeAccessToken.token.id}`)
      }
    },
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  })

  return async (args, api, extraOptions) => {
    // Mutex is used to prevent multiple refresh token requests when multiple calls fail on 401
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock()

    const userType = resolveUserType()

    // Capture the token this request authenticates with, so a 401 caused by a token that was rotated while the request was in flight is not blamed on the current session.
    const sentAccessToken = getActiveAccessToken(userType)

    // Mutex is unlocked, fire the request
    let result = await baseQuery(args, api, extraOptions)

    // App debugging - RTK Query
    // if (!IS_PRODUCTION && Capacitor.isNativePlatform()) {
    //   console.log(`QUERY(${baseUrl})--PROPS:`, JSON.stringify({ args, api, extraOptions }, null, 2))
    //   console.log(`QUERY(${baseUrl})--RESULT:`, JSON.stringify(result, null, 2))
    // }

    // if the request returns a 401 error, try to refresh the token
    if (result.error && is401(result.error)) {
      const activeAccessToken = getActiveAccessToken(userType)

      // The tokens were rotated while this request was in flight:
      // the 401 belongs to the old token, not the current session. Retry once.
      if (activeAccessToken && sentAccessToken?.token.id !== activeAccessToken.token.id) {
        return await baseQuery(args, api, extraOptions)
      }

      // If the request was authenticated with a magic-link (sessionStorage) token, there is no refresh path. Never fall
      // back to the localStorage refresh token (it may belong to a different user) — end the magic session instead.
      if (activeAccessToken?.source === STORAGE_TYPES.sessionStorage) {
        handleMagicSessionLogout(userType)
        return result
      }

      const refreshToken = getRefreshToken(userType)

      if (refreshToken) {
        // Has a refresh token for the userType
        // Checking whether the mutex is not locked yet to start the token refresh
        if (!mutex.isLocked()) {
          // Mutex is not locked, lock it so other calls will wait for the mutex to be released
          const release = await mutex.acquire()

          try {
            // try to get a new token based on the refresh token url for the current user type
            const refreshTokenUrl = getRefreshTokenUrlForUserType(userType)
            const refreshResult = await baseQuery(
              { url: `${API_URL}${refreshTokenUrl}`, method: Methods.POST, body: { refreshToken: refreshToken.id } },
              api,
              extraOptions
            )
            const refreshTokensResponse = refreshResult?.data as { data?: RefreshTokensResponse } | undefined

            if (refreshTokensResponse?.data) {
              // Refresh token was valid and received new tokens, set them in local storage
              const {
                tokens: { token, refreshToken }
              } = refreshTokensResponse.data
              setTokens({ token, refreshToken }, userType)

              // Retry the original request
              result = await baseQuery(args, api, extraOptions)
            } else {
              // Refresh token was invalid, logout
              handleLogout(userType)
            }
          } finally {
            // Release the mutex so other calls can start that are waiting for mutex unlock
            release()
          }
        } else {
          // If the mutex is locked, it means that another call is already refreshing the token
          await mutex.waitForUnlock()

          // Mutex was released, retry the original request
          result = await baseQuery(args, api, extraOptions)
        }
      } else {
        // No refresh token found, logout
        handleLogout(userType)
      }
    } else if (result.error && is403(result.error)) {
      const activeAccessToken = getActiveAccessToken(userType)
      const wasRotated = !!activeAccessToken && sentAccessToken?.token.id !== activeAccessToken.token.id

      // The backend only ever answers an unauthenticated request with a 401, so a 403 is a permission failure and never
      // ends the session. It can still be the tail of a session another request is re-authorizing — this one sent a token
      // that has since been rotated, or is being rotated right now — so give it the current token and retry once.
      if (mutex.isLocked() || wasRotated) {
        await mutex.waitForUnlock()
        result = await baseQuery(args, api, extraOptions)
      }
    }

    // Call finished, return the result
    return result
  }
}

/**
 * Type-guard to check if object is a valid Response object
 * @param {FetchBaseQueryError["data"]} data
 * @returns {data is Response}
 */
const isCustomResponse = (data: FetchBaseQueryError['data']): data is Response => {
  return (
    !!data && typeof data === 'object' && Object.keys(data).some((k) => k === 'success' || k === 'data' || k === 'message' || k === 'error')
  )
}

/**
 * Builds the Sentry context describing the request that failed. `meta.request` is populated even for FETCH_ERROR/TIMEOUT_ERROR,
 * so the method and path are available for network failures where the backend never responded.
 * @param {FetchBaseQueryMeta} [meta] - The fetchBaseQuery meta object, containing the request and (when received) the response
 * @param {unknown} [arg] - The RTK Query endpoint argument, useful to reproduce the failing call
 * @returns {RequestContext}
 */
const getRequestContext = (meta?: FetchBaseQueryMeta, arg?: unknown): RequestContext => {
  const url = meta?.request?.url
  let parsedUrl: URL | undefined

  try {
    parsedUrl = url ? new URL(url) : undefined
  } catch {
    parsedUrl = undefined
  }

  return {
    method: meta?.request?.method,
    url,
    path: parsedUrl?.pathname,
    query: parsedUrl?.search || undefined,
    httpStatus: meta?.response?.status,
    statusText: meta?.response?.statusText,
    arg
  }
}

/**
 * Builds the Sentry context describing the client at the time of failure. Distinguishes actionable outages from expected
 * network drops: a backgrounded app or an offline device produces the same opaque "Load failed" as a real API problem.
 * @returns {RequestContext}
 */
const getClientContext = (): Context => {
  if (typeof window === 'undefined') return { platform: Capacitor.getPlatform() }

  const { connection } = navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } }

  return {
    platform: Capacitor.getPlatform(),
    online: navigator.onLine,
    visibility: document.visibilityState,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink
  }
}

/**
 * Creates the `transformErrorResponse` handler for an RTK Query endpoint. Logs the failure with the backend error body,
 * the request that caused it and the client state, then returns the backend `message` for the UI.
 *
 * Pass it directly as the endpoint's `transformErrorResponse` so RTK Query supplies `meta` and `arg`.
 *
 * @param {IdentifierFormats} identifier - Structured log identifier, e.g. `[metrics:getPriceHistory]`
 * @param {{ severity?: Severity, criticality?: Criticality }} [options] - Overrides for the logged severity/criticality
 * @returns {(err: FetchBaseQueryError, meta?: FetchBaseQueryMeta, arg?: unknown) => string | undefined}
 * @example
 * transformErrorResponse: createErrorTransformer(`[${Flow.METRICS}:getPriceHistory]`)
 */
export const createErrorTransformer =
  (
    identifier: IdentifierFormats,
    options?: { severity?: Severity; criticality?: Criticality }
  ): ((err: FetchBaseQueryError, meta?: FetchBaseQueryMeta, arg?: unknown) => string | undefined) =>
  (err: FetchBaseQueryError, meta?: FetchBaseQueryMeta, arg?: unknown): string | undefined => {
    const backendError = 'data' in err && isCustomResponse(err.data) ? err.data : undefined
    const defaultMsg = `[${err.status}] Error while fetching query: ${identifier}`
    const returnValue = backendError ? backendError.errorCode || backendError.message : defaultMsg
    const request = getRequestContext(meta, arg)
    const client = getClientContext()
    const hasNoResponse = NO_RESPONSE_STATUSES.includes(err.status)
    const fetchErrorMessage = 'error' in err ? err.error : undefined
    const hasOpaqueTransportError = OPAQUE_TRANSPORT_ERRORS.some((transportError) =>
      (fetchErrorMessage ?? '').toLowerCase().includes(transportError)
    )

    // A cancelled request (unmount, refetch, navigation) is not a failure. The generic log() wraps strings in a fresh Error,
    // so Sentry's AbortError filter in beforeSend never sees these — filter them here instead.
    // Aborting during the body read surfaces as PARSING_ERROR rather than a NO_RESPONSE_STATUSES status, so the status is
    // not part of the check: `error` only ever holds a client-side throw, never a backend body.
    if (/abort/i.test(fetchErrorMessage ?? '')) return returnValue

    // The request produced no usable response and the browser named no cause, so there is nothing to act on. `navigator.onLine`
    // stays true for a connection that dies mid-request, so the online/visibility flags travel as attributes rather than gating
    // the count. A connection lost during the body read reports PARSING_ERROR, hence the status-independent message check.
    if (hasOpaqueTransportError || (hasNoResponse && (client.online === false || client.visibility === 'hidden'))) {
      trackSentryCount('query.request_dropped', {
        identifier,
        method: String(request.method),
        path: String(request.path),
        status: String(err.status),
        offline: client.online === false,
        backgrounded: client.visibility === 'hidden'
      })
      return returnValue
    }

    if (is401(err)) {
      trackSentryCount('query.session_expired', {
        identifier,
        method: String(request.method),
        path: String(request.path)
      })
      return returnValue
    }

    // The backend puts a machine-readable code in `errorCode`, or in `message` for the older endpoints
    const errorCode = [backendError?.errorCode, backendError?.message].find(isResponseCode)
    const status = err.status === 'PARSING_ERROR' && err.originalStatus >= 400 ? err.originalStatus : err.status
    const statusLabel = typeof status === 'number' ? `HTTP ${status}` : status
    const route = request.method && request.path ? `${request.method} ${request.path}` : identifier
    // A body fetchBaseQuery could not parse is kept verbatim in `data`; it carries the backend's own message, while
    // `error` only names the JSON syntax failure it tripped over.
    const unparsableBody =
      err.status === 'PARSING_ERROR' && typeof err.data === 'string' ? err.data.trim().slice(0, MAX_ERROR_BODY_LENGTH) : undefined
    const detail = backendError?.error || backendError?.message || unparsableBody || fetchErrorMessage || 'no error detail'

    log({
      error: `${statusLabel} on ${route}${errorCode ? ` (${errorCode})` : ''}: ${detail}`,
      identifier: (errorCode ? identifier.replace(/]$/, `:${errorCode}]`) : identifier) as IdentifierFormats,
      severity: options?.severity ?? (hasNoResponse ? Severity.WARNING : Severity.ERROR),
      criticality: options?.criticality,
      errorCode,
      // Group on endpoint + status + code so ids and query strings in the message don't fragment the issue
      fingerprint: [identifier, String(statusLabel), errorCode ?? 'no-code'],
      metaData: {
        request,
        client,
        response: {
          rtkStatus: err.status,
          ...(backendError ?? {}),
          ...(unparsableBody ? { body: unparsableBody } : {}),
          ...(fetchErrorMessage ? { fetchError: fetchErrorMessage } : {}),
          ...('originalStatus' in err ? { originalStatus: err.originalStatus } : {})
        }
      }
    })

    return returnValue
  }
